import React, { useRef, useState } from "react";
import FeatureIdeaComments from "./FeatureIdeaComments";
import {
    castFeatureIdeaVote,
    FeatureIdeaComment,
    FeatureIdeaSummary,
    fetchFeatureIdeaComments,
    formatRelativeTime,
    VoteType,
} from "../services/featureRequestsService";

type Props = {
    idea: FeatureIdeaSummary;
    onIdeaUpdated: (idea: FeatureIdeaSummary) => void;
};

function Avatar({ url, name }: { url: string | null; name: string }) {
    if (url) {
        return <img src={url} alt="" className="feature-idea-avatar" />;
    }
    const initial = name?.trim()?.charAt(0)?.toUpperCase() || "?";
    return (
        <span className="feature-idea-avatar feature-idea-avatar--placeholder" aria-hidden="true">
            {initial}
        </span>
    );
}

function ThumbIcon({ up, filled }: { up: boolean; filled: boolean }) {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            {up ? (
                <path
                    d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h10.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"
                    fill={filled ? "currentColor" : "none"}
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            ) : (
                <path
                    d="M10 15v4a3 3 0 0 0 3 3l4-9V2H6.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10zM17 2h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"
                    fill={filled ? "currentColor" : "none"}
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            )}
        </svg>
    );
}

export default function FeatureIdeaCard({ idea, onIdeaUpdated }: Props) {
    const [expanded, setExpanded] = useState(false);
    const [comments, setComments] = useState<FeatureIdeaComment[]>([]);
    const [loadingComments, setLoadingComments] = useState(false);
    const [voteBusy, setVoteBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const voteLock = useRef(false);
    const ideaRef = useRef(idea);
    ideaRef.current = idea;
    const readOnly = idea.status === "COMPLETED";

    const loadComments = async () => {
        setLoadingComments(true);
        setError(null);
        try {
            const page = await fetchFeatureIdeaComments(idea.id);
            setComments(page.content);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to load comments.");
        } finally {
            setLoadingComments(false);
        }
    };

    const toggleExpand = async () => {
        const next = !expanded;
        setExpanded(next);
        if (next && comments.length === 0) {
            await loadComments();
        }
    };

    const handleVote = async (voteType: VoteType) => {
        if (readOnly || voteLock.current) return;
        voteLock.current = true;
        setVoteBusy(true);
        setError(null);
        try {
            const result = await castFeatureIdeaVote(ideaRef.current.id, voteType);
            onIdeaUpdated({
                ...ideaRef.current,
                voteScore: result.voteScore,
                currentUserVote: result.currentUserVote,
            });
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to cast vote.");
        } finally {
            voteLock.current = false;
            setVoteBusy(false);
        }
    };

    return (
        <article className="feature-idea-card" data-testid={`feature-idea-card-${idea.id}`}>
            <div className="feature-idea-card-header">
                <div className="feature-idea-card-main">
                    <h2 className="feature-idea-card-title">{idea.title}</h2>
                    <div className="feature-idea-author-row">
                        <Avatar url={idea.authorAvatarUrl} name={idea.authorDisplayName} />
                        <strong>{idea.authorDisplayName}</strong>
                        {idea.authorCompanyName && (
                            <>
                                <span className="feature-idea-meta-sep">|</span>
                                <span>{idea.authorCompanyName}</span>
                            </>
                        )}
                        <span className="feature-idea-meta-sep">·</span>
                        <span>{formatRelativeTime(idea.createdAt)}</span>
                    </div>
                </div>
                <div className="feature-idea-votes" aria-label="Votes">
                    <span className="feature-idea-vote-score" data-testid={`feature-idea-vote-score-${idea.id}`}>
                        {idea.voteScore} {Math.abs(idea.voteScore) === 1 ? "vote" : "votes"}
                    </span>
                    <button
                        type="button"
                        className={`feature-idea-vote-btn${idea.currentUserVote === "UP" ? " active" : ""}`}
                        aria-label="Upvote"
                        aria-pressed={idea.currentUserVote === "UP"}
                        disabled={readOnly || voteBusy}
                        data-testid={`feature-idea-upvote-${idea.id}`}
                        onClick={() => handleVote("UP")}
                    >
                        <ThumbIcon up filled={idea.currentUserVote === "UP"} />
                    </button>
                    <button
                        type="button"
                        className={`feature-idea-vote-btn${idea.currentUserVote === "DOWN" ? " active" : ""}`}
                        aria-label="Downvote"
                        aria-pressed={idea.currentUserVote === "DOWN"}
                        disabled={readOnly || voteBusy}
                        data-testid={`feature-idea-downvote-${idea.id}`}
                        onClick={() => handleVote("DOWN")}
                    >
                        <ThumbIcon up={false} filled={idea.currentUserVote === "DOWN"} />
                    </button>
                </div>
            </div>
            <div>
                <button
                    type="button"
                    className="feature-idea-expand"
                    onClick={toggleExpand}
                    data-testid={`feature-idea-expand-${idea.id}`}
                >
                    {expanded ? "− Hide Full Description and Comments" : "+ Read Full Description and Comments"}
                </button>

                {expanded && (
                    <>
                        <p className="feature-idea-description">{idea.description}</p>
                        {loadingComments && <p className="feature-requests-loading">Loading comments…</p>}
                        {!loadingComments && (
                            <FeatureIdeaComments
                                ideaId={idea.id}
                                comments={comments}
                                readOnly={readOnly}
                                onCommentAdded={(comment) => {
                                    setComments((prev) => [...prev, comment]);
                                    onIdeaUpdated({
                                        ...idea,
                                        commentCount: idea.commentCount + 1,
                                    });
                                }}
                            />
                        )}
                    </>
                )}
                {error && (
                    <div className="feature-requests-error" role="alert">
                        {error}
                    </div>
                )}
            </div>
        </article>
    );
}
